// Shared subject-date formatting for tributes.
//
// PRIVACY RULE (do not casually break this):
//   * While the subject is ALIVE (date_of_death null/empty): expose the BIRTH
//     YEAR ONLY — never the full date of birth. This holds everywhere a date is
//     shown: the public tribute page, the AI conversation prompt, and the
//     compiled tribute alike.
//   * Once DECEASED (date_of_death set): expose full month-name dates as a range.
//
// Dependency-free (no date-fns). Date-only values like "1952-03-14" are parsed
// from their YYYY-MM-DD parts directly — NOT via new Date(str) — so a timezone
// never shifts the day. Month names come from an explicit array, no locale dep.

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type DateParts = { year: number; month: number; day: number };

// Parse the YYYY-MM-DD portion of a date string into numeric parts, or null if
// it is empty/malformed. Tolerates a trailing time component ("...T00:00:00Z").
function parseDateParts(value: string | null | undefined): DateParts | null {
  if (!value) return null;
  const datePart = value.split("T")[0];
  const segments = datePart.split("-");
  if (segments.length < 3) return null;
  const year = Number(segments[0]);
  const month = Number(segments[1]);
  const day = Number(segments[2]);
  if (!year || !month || !day) return null;
  if (month < 1 || month > 12) return null;
  return { year, month, day };
}

// "Month D, YYYY" from parsed parts, using the explicit month-name array.
function formatFullDate(parts: DateParts): string {
  return `${MONTH_NAMES[parts.month - 1]} ${parts.day}, ${parts.year}`;
}

// The year portion of a YYYY-MM-DD string, or null. For the AI prompt + compile
// paths to use while the subject is alive (year only, never the full DOB).
export function birthYearOnly(
  dateOfBirth: string | null | undefined,
): string | null {
  const parts = parseDateParts(dateOfBirth);
  return parts ? String(parts.year) : null;
}

// The display label for a tribute's dates, enforcing the privacy rule above.
//   * Both empty           -> null
//   * Deceased (DOD set)   -> "Month D, YYYY – Month D, YYYY" (en dash, spaces);
//                             if DOB is missing, just the formatted death date.
//   * Alive (DOD empty)    -> birth YEAR only (e.g. "1952"); never the full DOB.
export function publicDateLabel(
  dateOfBirth: string | null | undefined,
  dateOfDeath: string | null | undefined,
): string | null {
  const birth = parseDateParts(dateOfBirth);
  const death = parseDateParts(dateOfDeath);

  if (death) {
    const deathLabel = formatFullDate(death);
    return birth ? `${formatFullDate(birth)} – ${deathLabel}` : deathLabel;
  }

  // Alive: birth year only, never the full date of birth.
  if (birth) return String(birth.year);

  return null;
}
